import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, Users, CreditCard, ArrowRight, ClipboardList, Building2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import * as ordersApi from '../../api/orders.api';
import { errorMessage } from '../../api/client';
import { formatMoney, formatDate } from '../../utils/format';
import { useAuth } from '../../hooks/useAuth';

function AdminDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    ordersApi.listAdminOrders()
      .then((data) => { if (!cancelled) setOrders(data || []); })
      .catch((e) => { if (!cancelled) setErr(errorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const byStatus = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});
  const paidRevenue = orders
    .filter((o) => o.paymentStatus === 'PAID')
    .reduce((sum, o) => sum + (o.amount || 0), 0);

  const needsAssignment = orders.filter(
    (o) => o.paymentStatus === 'PAID' && !o.assignedVendor && o.status !== 'CANCELLED'
  );

  return (
    <>
      <PageHeader
        title={`Hi ${user?.name?.split(' ')[0] || 'there'} — let's get to work`}
        subtitle="Operational view: bookings to action, clinic assignments, support."
        actions={<Link to="/orders" className="btn btn-primary">View all bookings</Link>}
      />

      <div className="grid grid-4">
        <StatCard icon={CalendarCheck} tone="amber"   label="Total bookings"      value={orders.length} />
        <StatCard icon={ClipboardList} tone="brand"  label="Awaiting clinic" value={needsAssignment.length} deltaLabel="action needed" />
        <StatCard icon={CreditCard}   tone="emerald" label="Paid revenue"      value={formatMoney(paidRevenue)} />
        <StatCard icon={Users}        tone="indigo"  label="Active customers"   value={new Set(orders.map((o) => o.user?._id || o.user)).size} />
      </div>

      <div className="grid grid-2" style={{ marginTop: 'var(--s-4)' }}>
        <div className="card">
          <div className="card-title">
            <h3>Bookings awaiting a clinic</h3>
            <Link to="/orders" className="text-sm">All bookings <ArrowRight size={14} /></Link>
          </div>
          {loading ? (
            <LoadingSpinner />
          ) : needsAssignment.length === 0 ? (
            <EmptyState title="All clear" description="Every paid booking has a clinic assigned." />
          ) : (
            <div className="col">
              {needsAssignment.slice(0, 6).map((o) => (
                <Link key={o._id} className="list-row" to={`/orders/${o._id}`}>
                  <div>
                    <div className="bold">{o.orderNumber}</div>
                    <div className="muted text-sm">
                      {o.user?.name || o.user?.email} · {o.package?.name} · {formatDate(o.createdAt)}
                    </div>
                  </div>
                  <div className="row">
                    <StatusBadge status={o.status} />
                    <ArrowRight size={16} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title"><h3>Booking pipeline</h3></div>
          {Object.keys(byStatus).length === 0 ? (
            <EmptyState title="No bookings yet" />
          ) : (
            <div className="col">
              {['PAID','ASSIGNED','IN_PROGRESS','COMPLETED','PENDING_PAYMENT','CANCELLED'].map((s) => {
                const c = byStatus[s] || 0;
                const pct = orders.length ? Math.round((c / orders.length) * 100) : 0;
                return (
                  <div key={s}>
                    <div className="row between" style={{ marginBottom: 6 }}>
                      <StatusBadge status={s} />
                      <span className="bold">{c} <span className="muted text-xs">· {pct}%</span></span>
                    </div>
                    <div className="bar-track"><div className="bar-fill" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 'var(--s-4)' }}>
        <div className="card-title">
          <h3>Quick actions</h3>
          <span className="muted text-sm">Admin/staff scope · cannot access system settings or audit log</span>
        </div>
        <div className="row wrap">
          <Link to="/users" className="btn btn-outline"><Users size={16} /> Manage customers</Link>
          <Link to="/orders" className="btn btn-outline"><CalendarCheck size={16} /> Process bookings</Link>
          <Link to="/vendors" className="btn btn-outline"><Building2 size={16} /> Clinics</Link>
          <Link to="/payments" className="btn btn-outline"><CreditCard size={16} /> Payment status</Link>
        </div>
        {err && <div className="notice warning" style={{ marginTop: 'var(--s-3)' }}>{err}</div>}
      </div>
    </>
  );
}

export default AdminDashboard;
